import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import {
    FormControl,
    Icon,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    InputAdornment,
    IconButton,
} from '@mui/material'
import {
    ReactElement,
    useEffect,
    useMemo,
    useState,
    useRef,
    useCallback,
} from 'react'
import {
    getUserDataByEmail,
    inviteUser,
    updateUser,
} from '../../services/userServices'
import { IAddUser, IAddUserMandatoryFields } from './interfaces'
import { useAppSelector } from '../../redux/hook'
import { CloseOutlined, Search } from '@mui/icons-material'
import { useUnits } from '../../hooks/useUnits'
import UnitsPreferences from '../Layout/Navbar/UnitsPreferences'
import {
    defaultAddUserFormData,
    MODE,
    UserPermissions,
    USER_ROLES,
    RootOrgName,
} from '../../constants/constants'
import LocalizationPreferences from '../Layout/Navbar/LocalizationPreferences'
import { defaultLocalization } from '../../utils/DateAndTimeUtils'
import { useLocalization } from '../../hooks/useLocalization'
import { useTranslation } from 'react-i18next'
import PaperModalComponent from '../Common/PaperModalComponent'
import { getRoleAuthId } from '../../utils/UserManagementUtils'
import { usePermissions } from '../../hooks/usePermissions'

const adminAboveRoles: string[] = [
    USER_ROLES.SYSTEM_ADMIN,
    USER_ROLES.SYSTEM_READER,
    USER_ROLES.DEVICE_MANAGER,
]

export default function AddUser(props: Readonly<IAddUser>): ReactElement {
    const { t } = useTranslation()
    const {
        openAddOrEditUser,
        handleAddOrEditUser,
        readOnly: readOnlyProps,
    } = props

    const org = useAppSelector((state) => state.org.org)
    const permissions = useAppSelector((state) => state.org.permissions)
    const isSuperAdmin = permissions?.includes(UserPermissions.ORG_ADMIN)

    const { open, mode, formData: data } = openAddOrEditUser
    const { totalUsers } = data
    const role = mode === MODE.ADD && totalUsers === 0 ? 'Admin' : data.role
    const [formData, setFormData] = useState({ ...data, role })
    const [errors, setErrors] = useState<Partial<IAddUserMandatoryFields>>({})
    const [isUpdated, setIsUpdated] = useState<boolean>(true)
    const [isFetchingUser, setIsFetchingUser] = useState<boolean>(false)
    const lastFetchedEmail = useRef<string>('')
    const roles = useAppSelector((state) => state.roles.roles)
    const unitsList = useAppSelector((state) => state.user.unitsList)
    const { hasPermission } = usePermissions()
    //if user admin opens system admin, system reader or device manager. we need to show in readOnly mode
    const readOnly = useMemo(() => {
        if (
            hasPermission([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_ADMIN,
            ])
        ) {
            return readOnlyProps
        } else if (
            hasPermission(UserPermissions.USER_ADMIN) &&
            adminAboveRoles.includes(formData.role)
        ) {
            return true
        } else {
            return readOnlyProps
        }
    }, [readOnlyProps, formData.role, hasPermission])
    const userRolesList: { role: string; disabled: boolean }[] = useMemo(
        () => [
            { role: USER_ROLES.ADMIN, disabled: false },
            { role: USER_ROLES.WRITER, disabled: false },
            { role: USER_ROLES.READER, disabled: false },
            { role: USER_ROLES.ANALYST, disabled: false },
            { role: USER_ROLES.DEVICE_MANAGER, disabled: !isSuperAdmin },
            {
                role: USER_ROLES.SYSTEM_ADMIN,
                disabled: !isSuperAdmin || org?.name !== RootOrgName,
            },
            {
                role: USER_ROLES.SYSTEM_READER,
                disabled: !isSuperAdmin || org?.name !== RootOrgName,
            },
        ],
        [isSuperAdmin, org],
    )

    const initialUnits = useMemo(() => {
        const orgUnits = org?.orgUnits
        const userUnits = data?.units
        return (
            unitsList?.map((i) => ({
                ...i,
                selected:
                    userUnits?.[i?.id] ??
                    orgUnits?.units?.[i?.id] ??
                    i?.units?.[0],
            })) || []
        )
    }, [unitsList, org?.orgUnits, data?.units])

    const titleText = useMemo(() => {
        let title = ''

        if (mode === MODE.EDIT) {
            if (readOnly) {
                title = t('viewUser')
            } else {
                title = t('editUser')
            }
        } else {
            title = t('addUser')
        }
        return title
    }, [mode, readOnly, t])

    const { units, onChangeUnits, isUnitsUpdated, setUnits } =
        useUnits(initialUnits)

    // set initial localization values
    const initialValues = useMemo(() => {
        let localizationObj = defaultLocalization
        if (mode === MODE.EDIT && formData?.localization) {
            localizationObj = {
                ...formData.localization,
                language:
                    formData.localization?.language ??
                    org?.preferences?.localization?.language ??
                    'en',
            }
        } else if (org?.preferences?.localization) {
            localizationObj = {
                ...org.preferences.localization,
                language: org?.preferences.localization.language ?? 'en',
            }
        }

        // Ensure language property exists
        return {
            dateFormat: localizationObj.dateFormat,
            timeFormat: localizationObj.timeFormat,
            timeZone: localizationObj.timeZone,
            language: localizationObj.language ?? 'en',
        }
    }, [mode, formData, org])

    // localization hook
    const { localization, handleLocalizationChange, isLocalizationUpdated } =
        useLocalization(initialValues)

    useEffect(() => {
        if (role === formData.role && mode !== MODE.ADD) {
            setIsUpdated(false)
        } else {
            setIsUpdated(true)
        }
    }, [formData.role, mode, role])

    const handleChange = useCallback((event: SelectChangeEvent) => {
        setFormData((prev) => ({ ...prev, role: event.target.value }))
    }, [])

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = event.target
            setFormData((prev) => ({ ...prev, [name]: value }))
        },
        [],
    )

    const clearForm = useCallback(() => {
        setFormData({
            ...defaultAddUserFormData,
        })
        setErrors({})
    }, [])

    const fetchUserData = useCallback(async () => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        const currentEmail = formData.email?.trim()

        if (
            currentEmail &&
            emailRegex.test(currentEmail) &&
            currentEmail !== lastFetchedEmail.current
        ) {
            lastFetchedEmail.current = currentEmail
            setIsFetchingUser(true)
            const userData = await getUserDataByEmail(
                org?.id || '',
                currentEmail,
            )
            if (userData?.preferences?.units) {
                const userUnits = userData.preferences.units
                const updatedUnits =
                    unitsList?.map((i) => ({
                        ...i,
                        selected: userUnits?.[i?.id] ?? '',
                    })) || []
                setUnits(updatedUnits)
            }
            setIsFetchingUser(false)
        }
    }, [formData.email, unitsList, setUnits, org?.id])

    const handleEmailBlur = useCallback(async () => {
        await fetchUserData()
    }, [fetchUserData])

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault()
                fetchUserData()
            }
        },
        [fetchUserData],
    )

    const validateForm = useCallback(() => {
        let isValid = true
        const errors: Partial<IAddUserMandatoryFields> = {}

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        const isValidEmail = emailRegex.test(formData.email)
        if (!formData.email) {
            errors.email = t('emailRequired')
            isValid = false
        } else if (!isValidEmail) {
            errors.email = t('emailInvalid')
            isValid = false
        }

        if (!formData.role) {
            errors.role = t('roleRequired')
            isValid = false
        }

        setErrors(errors)
        return isValid
    }, [formData.email, formData.role, t])

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (validateForm()) {
                const updatedUints: { [id: string]: string } = {}
                units?.forEach((i) => {
                    updatedUints[i.id] = i.selected || ''
                })
                const role = getRoleAuthId(roles, formData.role)
                if (mode === MODE.ADD) {
                    await inviteUser(
                        {
                            email: formData.email,
                            roleAuth0Id: role,
                            preferences: {
                                units: updatedUints,
                                localization,
                            },
                        },
                        org?.id || '',
                    )
                } else {
                    const payload = {
                        email: formData.email,
                        roleAuth0Id: getRoleAuthId(roles, formData.role),
                        id: formData.id,
                        userorgId: formData.id,
                        preferences: {
                            units: updatedUints,
                            localization,
                        },
                    }
                    await updateUser(payload, org?.id || '')
                }

                clearForm()
                handleAddOrEditUser(true)
            }
        },
        [
            validateForm,
            units,
            roles,
            formData.role,
            formData.email,
            formData.id,
            mode,
            org?.id,
            localization,
            handleAddOrEditUser,
            clearForm,
        ],
    )

    return (
        <div>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <PaperModalComponent>
                    <form onSubmit={handleSubmit}>
                        <Box
                            sx={{
                                p: 4,
                                boxShadow: 4,
                                borderRadius: 4,
                                overflowY: 'auto',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '80%',
                                maxWidth: '1200px',
                                bgcolor: 'background.paper',
                                border: 2,
                                borderColor: 'primary.main',
                                maxHeight: '90vh',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '15px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Icon
                                    onClick={() => handleAddOrEditUser(false)}
                                    sx={{ marginRight: '8px' }}
                                    fontSize="small"
                                    id="modal-close-icon"
                                >
                                    <CloseOutlined
                                        sx={{
                                            color: '#000',
                                            marginTop: '-2px',
                                        }}
                                    />
                                </Icon>
                            </Box>
                            <Typography
                                variant="h6"
                                component="h2"
                                fontSize={18}
                                id="draggable-modal-title"
                                sx={{ cursor: 'move' }}
                            >
                                {titleText}
                            </Typography>
                            <Box sx={{ mt: 2, mb: 4 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 2, fontSize: '18px' }}
                                >
                                    {t('userDetails')}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: '16px',
                                    }}
                                >
                                    <TextField
                                        id="selected-org"
                                        label={t('selectedOrg')}
                                        variant="outlined"
                                        disabled
                                        value={org?.name}
                                        fullWidth
                                    />
                                    <TextField
                                        id="add-user-email"
                                        name="email"
                                        label={t('userEmailRequired')}
                                        variant="outlined"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        fullWidth
                                        disabled={
                                            mode === MODE.EDIT || readOnly
                                        }
                                        onBlur={handleEmailBlur}
                                        onKeyDown={handleKeyDown}
                                        InputProps={{
                                            endAdornment:
                                                mode === MODE.ADD ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={
                                                                fetchUserData
                                                            }
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            <Search />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                        }}
                                    />
                                    <FormControl
                                        fullWidth
                                        error={!!errors.role}
                                        disabled={readOnly}
                                    >
                                        <InputLabel>
                                            {t('userRoleRequired')}
                                        </InputLabel>
                                        <Select
                                            labelId="demo-simple-select-label"
                                            id="add-user-role"
                                            name="role"
                                            value={formData.role}
                                            label={t('userRoleRequired')}
                                            onChange={handleChange}
                                            disabled={
                                                totalUsers === 0 ||
                                                (totalUsers === 1 &&
                                                    mode === MODE.EDIT) ||
                                                readOnly
                                            }
                                        >
                                            {userRolesList?.map((i) => (
                                                <MenuItem
                                                    key={i.role}
                                                    value={i.role}
                                                    id={i.role}
                                                    disabled={i.disabled}
                                                    sx={{
                                                        display: i.disabled
                                                            ? 'none'
                                                            : 'flex',
                                                    }}
                                                >
                                                    {t(i.role)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.role && (
                                            <Typography
                                                variant="caption"
                                                sx={{ ml: '14px', mt: '3px' }}
                                                color="error"
                                            >
                                                {errors.role}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Box>
                            </Box>
                            <Box sx={{ mb: 4 }}>
                                <LocalizationPreferences
                                    initialPreferences={localization}
                                    handleLocalizationChange={
                                        handleLocalizationChange
                                    }
                                    disabled={readOnly}
                                />
                            </Box>
                            <Box>
                                <UnitsPreferences
                                    units={units}
                                    onChangeUnits={onChangeUnits}
                                    disabled={readOnly}
                                />
                            </Box>
                            <Button
                                sx={{
                                    mt: 4,
                                    maxWidth: '200px',
                                    alignSelf: 'center',
                                }}
                                variant="contained"
                                id="add-user-submit-button"
                                disabled={
                                    (!isUpdated &&
                                        !isUnitsUpdated &&
                                        !isLocalizationUpdated) ||
                                    isFetchingUser ||
                                    readOnly
                                }
                                type="submit"
                            >
                                {t('submit')}
                            </Button>
                        </Box>
                    </form>
                </PaperModalComponent>
            </Modal>
        </div>
    )
}
